import { Component, computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { AccountInfo, InteractionStatus } from '@azure/msal-browser';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { filter } from 'rxjs';
import { Navbar } from './components/navbar/navbar';
import { Footer } from './components/footer/footer';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly msalService = inject(MsalService);
  private readonly msalBroadcastService = inject(MsalBroadcastService);

  private readonly account = signal<AccountInfo | null>(this.msalService.instance.getActiveAccount());
  readonly displayName = computed(() => this.account()?.name ?? this.account()?.username ?? '');
  readonly email = computed(() => this.account()?.username ?? '');
  readonly initials = computed(() => {
    const name = this.displayName();
    if (!name) {
      return '';
    }
    const parts = name.trim().split(/\s+/);
    return parts.length === 1
      ? parts[0].slice(0, 2).toUpperCase()
      : (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  });

  constructor() {
    // inProgress$ is a BehaviorSubject, so a late subscriber (e.g. Navbar created after
    // login redirect already finished) still gets notified once interaction settles.
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status) => status === InteractionStatus.None),
        takeUntilDestroyed()
      )
      .subscribe(() => this.syncActiveAccount());
  }

  logout(): void {
    this.msalService.logoutRedirect();
  }

  private syncActiveAccount(): void {
    const instance = this.msalService.instance;
    if (!instance.getActiveAccount()) {
      const [firstAccount] = instance.getAllAccounts();
      if (firstAccount) {
        instance.setActiveAccount(firstAccount);
      }
    }
    this.account.set(instance.getActiveAccount());
  }
}

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, Navbar, Footer],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  protected readonly title = signal('atlas-app');
}
